import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import StatusPill from '../components/StatusPill';
import { groupEpisodes } from '../lib/longformGroups';

function getPostedSince(filter) {
  const now = new Date();
  if (filter === 'week') {
    const d = new Date(now);
    const day = d.getDay(); // 0 = Sunday
    d.setDate(d.getDate() - (day === 0 ? 6 : day - 1)); // back to Monday
    d.setHours(0, 0, 0, 0);
    return d.toISOString().slice(0, 10);
  }
  if (filter === 'month') {
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
  }
  return null; // all time — no filter
}

function timeAgo(dateString) {
  const diff = Date.now() - new Date(dateString).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  return `${Math.floor(hrs / 24)} days ago`;
}

const ENTITY_LABEL = {
  script:          'Script',
  live_production: 'Live Production',
  ai_production:   'AI Production',
};

const COLS = ['scripted', 'filming', 'filmed', 'editing', 'qual', 'approved', 'posted'];

export default function Dashboard() {
  const [stats,        setStats]        = useState([]);
  const [depth,        setDepth]        = useState([]);
  const [activity,     setActivity]     = useState([]);
  const [incomplete,   setIncomplete]   = useState([]);
  const [qualSummary,  setQualSummary]  = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [postedFilter, setPostedFilter] = useState('week');
  const [postedCount,  setPostedCount]  = useState(null);
  const navigate = useNavigate();

  const fetchPostedCount = useCallback(async (filter) => {
    const since = getPostedSince(filter);
    const qs = since ? `?since=${since}` : '';
    try {
      const res = await api.get(`/api/dashboard/posted-count${qs}`);
      setPostedCount(res.data.count);
    } catch {}
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, d, a, i, q] = await Promise.all([
        api.get('/api/dashboard/stats'),
        api.get('/api/dashboard/posting-depth'),
        api.get('/api/dashboard/recent-activity'),
        api.get('/api/dashboard/incomplete-alert'),
        api.get('/api/qual/summary'),
      ]);
      setStats(s.data);
      setDepth(d.data);
      setActivity(a.data);
      setIncomplete(i.data);
      setQualSummary(q.data);
    } catch (err) {
      console.error('Dashboard load failed:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => { fetchPostedCount(postedFilter); }, [postedFilter, fetchPostedCount]);

  const [pipeSort, setPipeSort] = useState({ col: 'client', dir: 'asc' });

  const sortedStats = useMemo(() => {
    if (!stats.length) return stats;
    const { col, dir } = pipeSort;
    return [...stats].sort((a, b) => {
      if (col === 'client') {
        const av = a.client.name.toLowerCase();
        const bv = b.client.name.toLowerCase();
        return dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      const aIsAI = a.client.productionTrack === 'AI';
      const bIsAI = b.client.productionTrack === 'AI';
      const av = (aIsAI ? a.ai : a.live)[col] ?? 0;
      const bv = (bIsAI ? b.ai : b.live)[col] ?? 0;
      return dir === 'asc' ? av - bv : bv - av;
    });
  }, [stats, pipeSort]);

  // Aggregate totals for stat cards
  const totalScriptsAvailable = stats.reduce((n, s) => n + (s.scriptsAvailable || 0), 0);
  const totalInProd   = stats.reduce((n, s) =>
    n + s.live.filming + s.live.filmed + s.live.editing + s.live.qual
      + s.ai.editing  + s.ai.qual, 0);
  const totalApproved = stats.reduce((n, s) => n + s.live.approved + s.ai.approved, 0);

  const depthItems = depth.filter(d => d.live.approved > 0 || d.ai.approved > 0);

  const today = new Date().toLocaleDateString('en-US', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  });

  return (
    <div className="main">
      <div className="topbar">
        <div>
          <div className="page-title">Dashboard</div>
          <div className="page-sub">{today}</div>
        </div>
        <div className="topbar-actions">
          <button className="btn-ghost" onClick={() => { loadAll(); fetchPostedCount(postedFilter); }} disabled={loading}>↺ Refresh</button>
        </div>
      </div>

      <div className="body">

        {/* Incomplete alert banner — D-120: a series' episodes appear ONCE as "<Series> (N episodes)" */}
        {incomplete.length > 0 && (() => {
          const labels = groupEpisodes(incomplete).map(e => e.kind === 'series'
            ? `${e.scriptName} (${e.episodes.length} episode${e.episodes.length !== 1 ? 's' : ''})`
            : (e.record.script?.scriptName || 'Riff entry'));
          return (
          <div className="alert-banner">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ color: '#d45555', fontSize: 16 }}>⚠</span>
              <span className="alert-text">
                {incomplete.length} Incomplete record{incomplete.length !== 1 ? 's' : ''} need a reshoot scheduled
                {' — '}
                {labels.slice(0, 3).join(', ')}
                {labels.length > 3 ? ` +${labels.length - 3} more` : ''}
              </span>
            </div>
            <button className="btn-danger" onClick={() => navigate('/live')}>View Incomplete</button>
          </div>
          );
        })()}

        {/* Stat cards */}
        <div className="stat-row">
          <div className="stat-card violet" title="Video cards at Scripted — approved and waiting to shoot">
            <div className="stat-label">Scripts Available</div>
            <div className="stat-value">{loading ? '—' : totalScriptsAvailable}</div>
            <div className="stat-sub">Approved, ready to shoot</div>
          </div>
          <div className="stat-card amber">
            <div className="stat-label">In Production</div>
            <div className="stat-value">{loading ? '—' : totalInProd}</div>
            <div className="stat-sub">Live + AI tracks</div>
          </div>
          <div className="stat-card green">
            <div className="stat-label">Approved & Ready</div>
            <div className="stat-value">{loading ? '—' : totalApproved}</div>
            <div className="stat-sub">Queued to post</div>
          </div>
          <div className="stat-card coral">
            <div className="stat-label">Total Posted</div>
            <div className="stat-value">{loading || postedCount === null ? '—' : postedCount}</div>
            <div style={{ display: 'flex', gap: 3, background: 'rgba(0,0,0,0.25)', borderRadius: 6, padding: 3 }}>
              {[['week', 'This Week'], ['month', 'This Month'], ['all', 'All Time']].map(([val, label]) => (
                <button
                  key={val}
                  onClick={() => setPostedFilter(val)}
                  style={{
                    flex: 1, padding: '5px 0', fontSize: 10, fontFamily: 'var(--font)',
                    fontWeight: 600, borderRadius: 4, border: 'none', cursor: 'pointer',
                    background: postedFilter === val ? 'rgba(255,255,255,0.18)' : 'transparent',
                    color:      postedFilter === val ? '#fff' : 'rgba(255,255,255,0.45)',
                    transition: 'background 0.15s, color 0.15s',
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Qual summary widget */}
        {qualSummary && (() => {
          const scriptTotal = qualSummary.scripts?.total || 0;
          const liveTotal   = qualSummary.liveVideos?.total || 0;
          const aiTotal     = qualSummary.aiVideos?.total || 0;
          const videoTotal  = liveTotal + aiTotal;
          const allClear    = scriptTotal === 0 && videoTotal === 0;

          const mergedByClient = {};
          (qualSummary.liveVideos?.byClient || []).forEach(c => {
            mergedByClient[c.clientName] = (mergedByClient[c.clientName] || 0) + c.count;
          });
          (qualSummary.aiVideos?.byClient || []).forEach(c => {
            mergedByClient[c.clientName] = (mergedByClient[c.clientName] || 0) + c.count;
          });
          const videoByClient = Object.entries(mergedByClient)
            .map(([name, count]) => ({ clientName: name, count }))
            .sort((a, b) => b.count - a.count);

          return (
            <div
              className="qual-widget"
              onClick={() => navigate('/qual')}
              style={{ cursor: 'pointer' }}
            >
              <div className="qual-widget-header">
                <span className="qual-widget-title">Qual Queue</span>
                <span className="qual-widget-arrow">→</span>
              </div>

              {allClear ? (
                <div className="qual-widget-clear">
                  <span style={{ color: '#4daa70', fontSize: 16 }}>✓</span>
                  Nothing in qual — all clear.
                </div>
              ) : (
                <div className="qual-widget-rows">
                  <div className="qual-widget-row">
                    <div className="qual-widget-label">
                      Scripts in Qual
                      <span className="qual-widget-total">{scriptTotal} total</span>
                    </div>
                    <div className="qual-widget-breakdown">
                      {(qualSummary.scripts?.byClient || []).map((c, i) => (
                        <span key={c.clientId}>
                          {i > 0 && <span className="qwb-sep">·</span>}
                          <span className="qwb-client">{c.clientName}:</span>
                          <span className="qwb-count">{c.count}</span>
                        </span>
                      ))}
                      {scriptTotal === 0 && <span style={{ color: 'var(--tx-muted)', fontSize: 12 }}>None</span>}
                    </div>
                  </div>

                  <div className="qual-widget-divider" />

                  <div className="qual-widget-row">
                    <div className="qual-widget-label">
                      Videos in Qual
                      <span className="qual-widget-total">
                        {videoTotal} total
                        {videoTotal > 0 && <span style={{ color: 'var(--tx-muted)', fontWeight: 400 }}> ({liveTotal} Live · {aiTotal} AI)</span>}
                      </span>
                    </div>
                    <div className="qual-widget-breakdown">
                      {videoByClient.map((c, i) => (
                        <span key={c.clientName}>
                          {i > 0 && <span className="qwb-sep">·</span>}
                          <span className="qwb-client">{c.clientName}:</span>
                          <span className="qwb-count">{c.count}</span>
                        </span>
                      ))}
                      {videoTotal === 0 && <span style={{ color: 'var(--tx-muted)', fontSize: 12 }}>None</span>}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })()}

        {/* Two-column main layout */}
        <div className="two-col">

          {/* Pipeline table */}
          <div className="card">
            <div className="card-header">
              <div>
                <div className="card-title">Pipeline by Project</div>
                <div className="card-sub">Live count at each stage — updated in real time</div>
              </div>
            </div>
            <div className="pipeline-table">
              <div className="pt-head">
                {['client', ...COLS].map(col => {
                  const active = pipeSort.col === col;
                  const label = col === 'client' ? 'Project' : col.charAt(0).toUpperCase() + col.slice(1);
                  return (
                    <div
                      key={col}
                      className="pt-th"
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => setPipeSort(prev => ({ col, dir: prev.col === col && prev.dir === 'asc' ? 'desc' : 'asc' }))}
                    >
                      {label}
                      <span style={{ marginLeft: 4, fontSize: 9, opacity: active ? 1 : 0.25, color: active ? 'var(--violet)' : 'inherit' }}>
                        {active && pipeSort.dir === 'desc' ? '▼' : '▲'}
                      </span>
                    </div>
                  );
                })}
              </div>

              {sortedStats.map(row => {
                const isAI = row.client.productionTrack === 'AI';
                const counts = isAI
                  ? { scripted: row.ai.scripted, filming: null, filmed: null,
                      editing: row.ai.editing, qual: row.ai.qual,
                      approved: row.ai.approved, posted: row.ai.posted }
                  : { scripted: row.live.scripted, filming: row.live.filming,
                      filmed: row.live.filmed, editing: row.live.editing,
                      qual: row.live.qual, approved: row.live.approved,
                      posted: row.live.posted };
                const trackLabel = isAI
                  ? `${row.client.serviceType} · AI`
                  : row.client.serviceType;
                return (
                  <div key={row.client.id} className="pt-row">
                    <div>
                      <div className="pt-client">{row.client.name}</div>
                      <div className="pt-type">{trackLabel}</div>
                    </div>
                    {COLS.map(col => (
                      <div key={col} className="pt-cell">
                        {counts[col] === null || counts[col] === 0
                          ? <span className="pt-count zero">—</span>
                          : <span className="pt-count">{counts[col]}</span>
                        }
                      </div>
                    ))}
                  </div>
                );
              })}

              {!loading && stats.length === 0 && (
                <div style={{ padding: 24, color: 'var(--tx-muted)', fontSize: 13, textAlign: 'center' }}>
                  No active clients
                </div>
              )}
            </div>
          </div>

          {/* Right column */}
          <div className="right-col">

            {/* Posting depth */}
            <div className="card">
              <div className="card-header">
                <div>
                  <div className="card-title">Posting Depth</div>
                  <div className="card-sub">Approved videos queued per project</div>
                </div>
              </div>

              {depthItems.map(d => {
                const isAI     = d.client.productionTrack === 'AI';
                const count    = isAI ? d.ai.approved : d.live.approved;
                const track    = isAI ? 'AI' : 'Live';
                const fillCls  = count <= 3 ? 'fill-red' : count <= 7 ? 'fill-amber' : 'fill-green';
                const barWidth = Math.min(Math.round((count / 14) * 100), 100);
                return (
                  <div key={d.client.id} className="depth-item">
                    <div className="depth-label">
                      {d.client.name}
                      <span className="depth-track">{track}</span>
                    </div>
                    <div className="depth-bar-bg">
                      <div className={`depth-bar-fill ${fillCls}`} style={{ width: `${barWidth}%` }} />
                    </div>
                    <div className="depth-count">
                      {count} video{count !== 1 ? 's' : ''} · ~{count} day{count !== 1 ? 's' : ''}
                    </div>
                  </div>
                );
              })}

              {depthItems.length === 0 && !loading && (
                <div style={{ padding: '16px 20px', color: 'var(--tx-muted)', fontSize: 12 }}>
                  No approved videos in queue
                </div>
              )}

              <div className="depth-legend">
                <div className="legend-item">
                  <div className="legend-dot" style={{ background: '#d45555' }} />≤3 Low
                </div>
                <div className="legend-item">
                  <div className="legend-dot" style={{ background: '#d4a855' }} />4–7 Mid
                </div>
                <div className="legend-item">
                  <div className="legend-dot" style={{ background: '#4daa70' }} />8+ Good
                </div>
              </div>
            </div>

            {/* Recent activity */}
            <div className="card">
              <div className="card-header">
                <div className="card-title">Recent Activity</div>
              </div>

              {activity.slice(0, 10).map(item => (
                <div key={item.id} className="activity-item">
                  <div className="activity-pills">
                    {item.newValue && <StatusPill status={item.newValue} />}
                    <span className="activity-arrow">←</span>
                    {item.oldValue && <StatusPill status={item.oldValue} />}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--tx-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {ENTITY_LABEL[item.entityType] || item.entityType}
                  </div>
                  <div className="activity-who">
                    {item.changedByMember?.name} · {timeAgo(item.changedAt)}
                  </div>
                </div>
              ))}

              {activity.length === 0 && !loading && (
                <div style={{ padding: '16px 20px', color: 'var(--tx-muted)', fontSize: 12 }}>
                  No recent activity
                </div>
              )}
            </div>

          </div>
        </div>

      </div>
    </div>
  );
}
